// Product analytics (Launch guardrails — "GA4 + backend event table +
// Admin Analytics Dashboard, never GA4 alone"). trackEvent() is the single
// call site every funnel touchpoint uses: it pushes to GTM's dataLayer (so
// GA4 sees it via the existing GTMPageView tag setup) AND POSTs the same
// event to the backend, which is the privacy-safe, always-available source
// of product truth.
//
// NEVER pass document text, filenames, chat text, or generated draft text
// as a property — the backend's ALLOWED_PROPERTY_KEYS allowlist silently
// drops anything not on it, but don't rely on that as the only safeguard;
// keep properties to the plan's approved key list (see KNOWN properties in
// app/services/common/analytics_event_service.py).
import { appConfig } from "@/config/app";
import { auth } from "@/lib/firebase";

const SESSION_ID_KEY = "hoalaw_anonymous_session_id";

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** An RFC 4122 v4 UUID even where crypto.randomUUID is missing (non-secure
 *  contexts): the backend only stores ids in UUID form. */
export function uuidV4(): string {
  const c: Partial<Crypto> | undefined = typeof crypto !== "undefined" ? crypto : undefined;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Stable per-browser id, persisted across signup (plan's "identity
 *  stitching" requirement — landing-page events tie to the authenticated
 *  user's later product events via this same id, never regenerated). */
export function getAnonymousSessionId(): string {
  if (typeof window === "undefined") return "server";
  try {
    let id = window.localStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = generateId();
      window.localStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return generateId(); // localStorage unavailable (private mode, etc.) — best-effort
  }
}

const REVIEW_INTENT_KEY = "hoalaw_review_intent";

/** Captures the `?intent=` param a landing page CTA attached to the
 *  /signup or /login redirect (e.g. /review?intent=violation_response),
 *  persisting it for the rest of the browser tab's session so the
 *  eventual review_upload_started/review_completed events can attribute
 *  conversion back to the acquisition query class (plan's "Conversion by
 *  query class" measurement). Session-scoped, not localStorage — this is
 *  a single visit's intent, not a durable user property. */
export function captureReviewIntent(searchParams: URLSearchParams): void {
  const intent = searchParams.get("intent");
  if (!intent) return;
  try {
    window.sessionStorage.setItem(REVIEW_INTENT_KEY, intent);
  } catch {
    // sessionStorage unavailable — attribution is best-effort only.
  }
}

export function getReviewIntent(): string | undefined {
  try {
    return window.sessionStorage.getItem(REVIEW_INTENT_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export type AnalyticsProperties = Partial<{
  region: string;
  domain: string;
  page_path: string;
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  acquisition_page: string;
  query_category: string;
  device_type: string;
  entitlement_state: string;
  doc_type: string;
  generator_type: string;
  tool: string;
  intent: string;
  entry_surface: string;
  landing_page: string;
  user_role: string;
  user_role_view: string;
  is_new_user: boolean;
  subscription_status: string;
  // Forms & Templates — closed enums and opaque ids only. Never titles,
  // filenames, search text, field values, or error messages.
  form_code: string;
  catalog_category: string;
  action: "create" | "download" | "view_text";
  kind: "static" | "guided";
  step: "overview" | "fill" | "preview" | "done";
  failure_class: "validation" | "not_found" | "not_available" | "server" | "network";
  results_count_bucket: "0" | "1_5" | "6_plus";
  template_session_id: string;
  /** One id per form action attempt (reused only for a transport retry). */
  form_action_id: string;
  /** The chat ANSWER a template or next step came from (backend message id). */
  origin_message_id: number;
  // Question Intelligence: what a chat answer's next step offered.
  product_capability: "lexi" | "forms_templates" | "governing_documents" | "document_review" | "communication_drafting" | "none";
  action_type: "open_form" | "open_communication" | "open_review" | "open_information";
  target_type: "form_code" | "communication_type" | "review" | "info_link";
  // Acquisition: this tab's visit, the browser-link context, and entry into a
  // major product capability. `destination` is a closed list on the backend —
  // destination tracking is not page-view tracking.
  visit_id: string;
  link_context: "signup" | "sign_in";
  destination: "document_review" | "governing_documents";
  // Public product-page CTAs (landing_cta_clicked) — closed lists on the backend too.
  cta: "signup" | "pricing" | "review_preview" | "cross_link" | "how_it_works";
  cta_target: string;
  cta_position: "hero" | "footer" | "inline";
  // Governing-document upload funnel (lib/uploadFunnel.ts) — closed enums and the
  // per-attempt telemetry id only. Never file names or document contents.
  upload_attempt_id: string;
  upload_location: "page" | "slot" | "help" | "error" | "chat";
  file_kind: string;
  size_bucket: string;
  validation_reason: "type" | "size" | "empty";
  network_failure: "network" | "timeout" | "aborted";
}>;

/** Where a template interaction began. Must stay within the backend's
 *  ENTRY_SURFACES enum (app/services/common/question_taxonomy.py) — an
 *  unknown value is silently dropped server-side. */
export type TemplateEntrySurface =
  | "library"
  | "chat_suggestion"
  | "chat_chip"
  | "review_action"
  | "tools_hub"
  | "generate_start";

/** The backend id of a chat answer, for linking a template or next step to
 *  it — or undefined for a message that has none yet (a still-streaming
 *  reply carries a temporary client id). The server re-validates it. */
export function analyticsMessageId(id: string | number | undefined | null): number | undefined {
  const n = typeof id === "number" ? id : Number(id);
  return Number.isSafeInteger(n) && n > 0 && n <= 2_147_483_647 ? n : undefined;
}

/** Opaque correlation id (template_session_id / form_action_id). */
export function newCorrelationId(): string {
  return generateId();
}

/** The signed-in user's ID token, so the backend can record WHICH ACCOUNT did something (its
 *  /api/analytics/event endpoint attaches user_id when it receives one). Without it every event from a
 *  signed-in user was stored as anonymous. Best effort: a visitor who isn't signed in, or any error, just
 *  means the event is recorded without an account, exactly as before. */
async function currentIdToken(): Promise<string | null> {
  try {
    return (await auth.currentUser?.getIdToken()) ?? null;
  } catch {
    return null;
  }
}

const SEND_TIMEOUT_MS = 2000;

/** POSTs one event to the backend and resolves whether it was stored (2xx).
 *  Never throws, and gives up after 2 s. `idToken` sends a specific account's
 *  token (the signup bridge has one while the user is about to be signed
 *  out); otherwise the signed-in user's token is used, if any. */
export async function sendEvent(
  eventName: string,
  properties: AnalyticsProperties = {},
  options: { idToken?: string } = {},
): Promise<boolean> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), SEND_TIMEOUT_MS) : null;
  try {
    const base = import.meta.env.DEV ? window.location.origin : (import.meta.env.VITE_API_BASE_URL || window.location.origin);
    const fullProperties = { region: appConfig.state, ...properties };
    const token = options.idToken ?? (await currentIdToken());
    const response = await fetch(`${base}/api/analytics/event`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        event_name: eventName,
        anonymous_session_id: getAnonymousSessionId(),
        region: appConfig.state,
        properties: fullProperties,
      }),
      keepalive: true, // survives a navigation/unload right after the call
      ...(controller ? { signal: controller.signal } : {}),
    });
    return Boolean(response?.ok);
  } catch {
    // Network error, ad-blocker, offline, timeout -- tracking never surfaces to the user.
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Fire-and-forget by design — analytics must never block or fail the
 *  user's actual action. Both the GTM push and the backend POST are
 *  wrapped so a tracking failure is silent. */
export function trackEvent(eventName: string, properties: AnalyticsProperties = {}): void {
  const fullProperties = { region: appConfig.state, ...properties };

  try {
    const w = window as unknown as { dataLayer?: Record<string, unknown>[] };
    if (Array.isArray(w.dataLayer)) {
      w.dataLayer.push({ event: eventName, ...fullProperties });
    }
  } catch {
    // GTM not loaded / blocked — fine, backend event still fires below.
  }

  void sendEvent(eventName, properties);
}

// ---------------------------------------------------------------------------
// Acquisition: how a visitor found us (Admin -> User Details)
// ---------------------------------------------------------------------------
//
// One site_visit_started per browser-TAB session (sessionStorage), shared with
// the Astro pages, which are served from this same origin and use the same
// keys. Two separate keys so the order code runs in can't suppress the visit:
// the tab's visit_id is created by whichever code needs it first (React runs
// child effects before App's, so /generate/start may need it first), and the
// "recorded" flag only marks that site_visit_started was sent. Multiple tabs
// record multiple visits, by design. Privacy: the referrer is reduced to its
// origin, the landing page to a path, and only utm_source/medium/campaign are
// kept; the backend reduces again before storing.

const VISIT_ID_KEY = "hoalaw_visit_id_v1";
const SITE_VISIT_RECORDED_KEY = "hoalaw_site_visit_v1";
const SIGNUP_LINK_PENDING_KEY = "hoalaw_signup_link_pending_v1";
const LINKED_KEY_PREFIX = "hoalaw_linked_v1_";
const OWN_HOSTS = ["davis-stirling-act.ai", "floridahoalaw.ai", "texashoalaw.ai"];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign"] as const;

/** This tab's visit id, created on first use; undefined if storage is unavailable. */
export function getVisitId(): string | undefined {
  try {
    let id = window.sessionStorage.getItem(VISIT_ID_KEY);
    if (!id) {
      id = uuidV4();
      window.sessionStorage.setItem(VISIT_ID_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

function isOwnHost(hostname: string, currentHostname: string): boolean {
  const host = hostname.toLowerCase().replace(/^www\./, "");
  const current = currentHostname.toLowerCase().replace(/^www\./, "");
  return host === current || OWN_HOSTS.some((own) => host === own || host.endsWith(`.${own}`));
}

/** The privacy-reduced properties of a site visit. Pure, for tests. */
export function siteVisitProperties(href: string, referrer: string): AnalyticsProperties {
  const props: AnalyticsProperties = {};
  try {
    const page = new URL(href);
    props.landing_page = page.pathname;
    for (const key of UTM_KEYS) {
      const value = page.searchParams.get(key)?.trim();
      if (value) props[key] = value;
    }
    if (referrer) {
      const ref = new URL(referrer);
      if ((ref.protocol === "http:" || ref.protocol === "https:") && !isOwnHost(ref.hostname, page.hostname)) {
        props.referrer = ref.origin;
      }
    }
  } catch {
    // Malformed URL — keep whatever was already extracted.
  }
  return props;
}

/** Records this tab's visit once (the flag is set before sending: no
 *  duplicates, and a lost send is not retried). */
export function recordSiteVisit(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.sessionStorage.getItem(SITE_VISIT_RECORDED_KEY)) return;
    window.sessionStorage.setItem(SITE_VISIT_RECORDED_KEY, "1");
  } catch {
    // Storage blocked: record this page load's visit without dedupe.
  }
  const visitId = getVisitId();
  trackEvent("site_visit_started", {
    ...siteVisitProperties(window.location.href, document.referrer),
    ...(visitId ? { visit_id: visitId } : {}),
  });
}

interface PendingSignupLink {
  uid: string;
  region: string;
  visit_id?: string;
}

function readPendingSignupLink(): PendingSignupLink | null {
  try {
    const raw = window.localStorage.getItem(SIGNUP_LINK_PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingSignupLink) : null;
  } catch {
    return null;
  }
}

function clearPendingSignupLink(uid: string): void {
  try {
    if (readPendingSignupLink()?.uid === uid) window.localStorage.removeItem(SIGNUP_LINK_PENDING_KEY);
  } catch {
    /* storage unavailable */
  }
}

/** The email-signup bridge (account_browser_linked, link_context "signup").
 *  Email signup signs the new user out straight away, so without this the
 *  browser they arrived in is never tied to the account. Called with the new
 *  user BEFORE signOut. Best-effort observational telemetry: it never
 *  throws, waits at most ~2 s, and a failure leaves a pending marker that is
 *  retried from this same browser at the next sign-in (linkSignedInBrowser).
 *  Retry beats dedupe here: duplicates are harmless, a missing link breaks
 *  attribution. */
export async function linkSignupBrowser(user: { uid: string; getIdToken: () => Promise<string> }): Promise<void> {
  const visitId = getVisitId();
  const pending: PendingSignupLink = { uid: user.uid, region: appConfig.state, ...(visitId ? { visit_id: visitId } : {}) };
  try {
    window.localStorage.setItem(SIGNUP_LINK_PENDING_KEY, JSON.stringify(pending));
  } catch {
    /* storage unavailable — still try once */
  }
  let idToken: string;
  try {
    idToken = await user.getIdToken();
  } catch {
    return;
  }
  const ok = await sendEvent(
    "account_browser_linked",
    { link_context: "signup", ...(visitId ? { visit_id: visitId } : {}) },
    { idToken },
  );
  if (ok) clearPendingSignupLink(user.uid);
}

/** Called for every verified signed-in session. (a) Resends a signup link
 *  that failed earlier — only for the same account and THIS app's region (a
 *  marker from another state is left alone). (b) Records which browser this
 *  signed-in tab uses (link_context "sign_in"), once per tab per account,
 *  marking it done only after the backend stored it. A sign_in link only
 *  counts towards acquisition near the regional account's creation; later
 *  ones can never rewrite it. */
export async function linkSignedInBrowser(uid: string): Promise<void> {
  const pending = readPendingSignupLink();
  if (pending && pending.uid === uid && pending.region === appConfig.state) {
    const ok = await sendEvent("account_browser_linked", {
      link_context: "signup",
      ...(pending.visit_id ? { visit_id: pending.visit_id } : {}),
    });
    if (ok) clearPendingSignupLink(uid);
  }
  const linkedKey = `${LINKED_KEY_PREFIX}${uid}`;
  try {
    if (window.sessionStorage.getItem(linkedKey)) return;
  } catch {
    /* storage unavailable — send without dedupe */
  }
  const visitId = getVisitId();
  const ok = await sendEvent("account_browser_linked", {
    link_context: "sign_in",
    ...(visitId ? { visit_id: visitId } : {}),
  });
  if (ok) {
    try {
      window.sessionStorage.setItem(linkedKey, "1");
    } catch {
      /* storage unavailable */
    }
  }
}

/** Entry into a major product capability that has no other entry event
 *  (product_destination_visited). Not page-view tracking: the backend accepts
 *  only its closed list of destinations. */
export function recordDestinationEntry(destination: "document_review" | "governing_documents"): void {
  const visitId = getVisitId();
  trackEvent("product_destination_visited", { destination, ...(visitId ? { visit_id: visitId } : {}) });
}

/** This tab's visit id as an event property, for destination-entry events
 *  (library_viewed, wizard_started, communication_draft_started), so a
 *  visitor's path inside the app can be tied to the visit they arrived on. */
export function visitIdProperty(): { visit_id?: string } {
  const visitId = getVisitId();
  return visitId ? { visit_id: visitId } : {};
}
